import React, { useState } from 'react';
import { Box, TextField, Alert, Typography, FormControlLabel, Checkbox, Link } from '@mui/material';
import { VpnKey, Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton } from '@mui/material';

interface PATConfigurationProps {
    onTokenChange?: (token: string) => void;
    existingToken?: string;
}

const PATConfiguration: React.FC<PATConfigurationProps> = ({
    onTokenChange,
    existingToken
}) => {
    const [token, setToken] = useState(existingToken || '');
    const [showToken, setShowToken] = useState(false);
    const [requiredScopes] = useState([
        'repo',
        'read:org',
        'workflow'
    ]);

    const handleTokenChange = (value: string) => {
        setToken(value);
        onTokenChange?.(value);
    };

    const isValidToken = token.startsWith('ghp_') || token.startsWith('github_pat_');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VpnKey /> Personal Access Token Configuration
            </Typography>

            <Alert severity="info">
                A Personal Access Token (PAT) is required to authenticate with GitHub. 
                The token will be securely stored and encrypted.
            </Alert>

            <TextField
                label="Personal Access Token"
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => handleTokenChange(e.target.value)}
                required
                fullWidth
                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                error={token.length > 0 && !isValidToken}
                helperText={
                    token.length > 0 && !isValidToken 
                        ? 'Invalid token format. Must start with ghp_ or github_pat_'
                        : 'Enter your GitHub Personal Access Token'
                }
                InputProps={{
                    endAdornment: (
                        <IconButton
                            onClick={() => setShowToken(!showToken)}
                            edge="end"
                        >
                            {showToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    ),
                    style: { fontFamily: showToken ? 'monospace' : 'inherit' }
                }}
            />

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Required Token Scopes:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {requiredScopes.map(scope => (
                        <FormControlLabel
                            key={scope}
                            control={<Checkbox checked disabled />}
                            label={
                                <Box>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
                                        {scope}
                                    </Typography>
                                    <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                                        {scope === 'repo' && 'Full control of private repositories'}
                                        {scope === 'read:org' && 'Read org and team membership'}
                                        {scope === 'workflow' && 'Update GitHub Action workflows'}
                                    </Typography>
                                </Box>
                            }
                        />
                    ))}
                </Box>
            </Box>

            <Alert severity="warning">
                <Typography variant="body2">
                    <strong>How to create a Personal Access Token:</strong>
                    <br />
                    1. Go to GitHub Settings → Developer settings → Personal access tokens → Tokens (classic)
                    <br />
                    2. Click "Generate new token (classic)"
                    <br />
                    3. Select the required scopes listed above
                    <br />
                    4. Set an appropriate expiration date
                    <br />
                    5. Copy the generated token and paste it here
                    <br />
                    <Link 
                        href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token" 
                        target="_blank" 
                        rel="noopener noreferrer"
                    >
                        View GitHub Documentation
                    </Link>
                </Typography>
            </Alert>

            {token && isValidToken && (
                <Alert severity="success">
                    Token format is valid. The token will be encrypted and securely stored.
                </Alert>
            )}
        </Box>
    );
};

export default PATConfiguration;